import { FilterChip, FilterGroup } from '@src/components/shared/FilterChip';
import { PageHeader } from '@src/components/shared/PageHeader';
import { Badge } from '@src/components/ui/badge';
import { Card } from '@src/components/ui/card';
import { Dialog, DialogBody, DialogCloseButton, DialogDescription, DialogHeader, DialogTitle } from '@src/components/ui/dialog';
import { Input } from '@src/components/ui/input';
import { Select } from '@src/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@src/components/ui/table';
import { COVERAGE_OUTCOMES } from '@src/lib/coverage';
import { errorMessage } from '@src/lib/errors';
import { formatCost, formatDuration, formatRunDate, formatUtcTooltip, statusBadgeVariant, statusLabel } from '@src/lib/runFormat';
import { resources } from '@src/resources';
import {
  type DateWindow,
  ENRICHMENT_SCOPES,
  type EnrichmentListRow,
  type EnrichmentsQueryParams,
  fetchEnrichments,
  fetchRunsForEnrichment,
  hasEnrichmentDetailRoute,
  Outcome,
  outcomeLabel,
  type RunRow,
  scopeLabel,
} from '@src/services/opsService';
import { useQuery } from '@tanstack/react-query';
import { ZoomIn } from 'lucide-react';
import { type ChangeEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

/** Same frontend-only 'custom' addition to the API's window enum as the Runs screen. */
type WindowSelection = DateWindow | 'custom';

const DEFAULT_WINDOW: WindowSelection = 'all';

const WINDOW_OPTIONS: { value: WindowSelection; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom' },
];

/** `enrichments.result` can only ever hold an outcome that a successful run actually writes --
 * ERROR/EXCLUDED_FROM_SAMPLE (run-level-only outcomes) never appear here, same reasoning as the
 * Coverage screen's `COVERAGE_OUTCOMES`. Built from the full `Outcome` enum (not just the reachable
 * subset) so every member always has a chip -- the unreachable ones render disabled with a tooltip,
 * rather than silently missing, so "why isn't ERROR here" doesn't look like a bug. */
const REACHABLE_OUTCOMES = new Set<string>(COVERAGE_OUTCOMES);

const OUTCOME_OPTIONS: { value?: string; label: string; disabled: boolean }[] = [
  { value: undefined, label: 'All', disabled: false },
  ...Object.values(Outcome).map((value) => ({
    value,
    label: outcomeLabel(value),
    disabled: !REACHABLE_OUTCOMES.has(value),
  })),
];


/** `EnrichmentListRow.timestamp`/`publishedAt` are unix seconds (matches `EnrichmentRecord`, the
 * schema-driven `/enrichment/*` engine's own shape) -- runFormat's date helpers take an ISO string,
 * same as `RunRow.createdAt`, so this is the one-line bridge between the two. */
function toIso(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toISOString();
}

/** Error/skip-reason text only -- mirrors Runs.tsx's `resultText`. */
function resultText(row: RunRow): string {
  return row.error ?? row.message ?? '—';
}

/** Links a contributing run to its enrichment detail view, exactly like Runs.tsx's own
 * `runDetailHref` -- duplicated rather than imported since it's a private 3-line helper there. */
function runDetailHref(row: RunRow): string {
  const params = new URLSearchParams({ runId: row.runId, propertyId: row.propertyId, scope: row.scope, createdAt: row.createdAt });
  return `/runs/detail?${params.toString()}`;
}

export function Enrichments() {
  // Every filter lives in the URL (not useState), matching the Runs screen -- shareable/bookmarkable,
  // survives a refresh or back/forward navigation.
  const [searchParams, setSearchParams] = useSearchParams();

  const setParam = (key: string, value: string | undefined) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === undefined || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  };

  const dateWindow = (searchParams.get('window') as WindowSelection | null) ?? DEFAULT_WINDOW;
  const scope = searchParams.get('scope') ?? undefined;
  const outcome = searchParams.get('outcome') ?? undefined;
  const propertyId = searchParams.get('propertyId') ?? '';
  const customFrom = searchParams.get('from') ?? '';
  const customTo = searchParams.get('to') ?? '';

  const setDateWindow = (value: WindowSelection) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === DEFAULT_WINDOW) {
          next.delete('window');
        } else {
          next.set('window', value);
        }
        if (value !== 'custom') {
          next.delete('from');
          next.delete('to');
        }
        return next;
      },
      { replace: true },
    );
  };
  const setScope = (value: string | undefined) => setParam('scope', value);
  const setOutcome = (value: string | undefined) => setParam('outcome', value);
  const setPropertyId = (value: string) => setParam('propertyId', value);
  const setCustomFrom = (value: string) => setParam('from', value);
  const setCustomTo = (value: string) => setParam('to', value);

  const [openRunsFor, setOpenRunsFor] = useState<{ scope: string; propertyId: string } | null>(null);

  const trimmedPropertyId = propertyId.trim();
  const isCustomWindow = dateWindow === 'custom';
  // datetime-local gives a value with no timezone offset (interpreted as local time by `new Date()`,
  // which is what we want -- the picker itself is already showing the user's local time).
  const customSince = isCustomWindow && customFrom ? new Date(customFrom).toISOString() : undefined;
  const customUntil = isCustomWindow && customTo ? new Date(customTo).toISOString() : undefined;

  const queryParams: EnrichmentsQueryParams = {
    window: isCustomWindow ? undefined : dateWindow,
    since: customSince,
    until: customUntil,
    scope,
    outcome,
    propertyId: trimmedPropertyId || undefined,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops-enrichments', queryParams],
    queryFn: () => fetchEnrichments(queryParams),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const hasActiveFilters =
    dateWindow !== DEFAULT_WINDOW || scope !== undefined || outcome !== undefined || trimmedPropertyId !== '';

  const clearFilters = () => setSearchParams(new URLSearchParams(), { replace: true });

  const resultsLabel = isLoading
    ? resources.common.loading
    : total > items.length
      ? `Showing ${items.length} of ${total.toLocaleString()} enrichments`
      : `${total.toLocaleString()} enrichment${total === 1 ? '' : 's'} matched`;

  const runsQuery = useQuery({
    queryKey: ['enrichment-contributing-runs', openRunsFor?.scope, openRunsFor?.propertyId],
    queryFn: () => fetchRunsForEnrichment(openRunsFor!.scope, openRunsFor!.propertyId),
    enabled: openRunsFor !== null,
  });
  const runs = runsQuery.data ?? [];

  return (
    <div>
      <PageHeader title={resources.pages.enrichments.title} subtitle={resources.pages.enrichments.subtitle} />

      <Card className="mb-4 p-4">
        <div className="flex flex-wrap items-end gap-6">
          <FilterGroup label={resources.enrichments.windowLabel}>
            {WINDOW_OPTIONS.map((opt) => (
              <FilterChip
                key={opt.value}
                label={opt.label}
                active={dateWindow === opt.value}
                onClick={() => setDateWindow(opt.value)}
              />
            ))}
          </FilterGroup>

          {isCustomWindow && (
            <FilterGroup label={resources.enrichments.customRangeLabel}>
              <Input
                type="datetime-local"
                value={customFrom}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setCustomFrom(event.target.value)}
                className="w-[190px]"
                aria-label={resources.enrichments.customFromLabel}
              />
              <span className="text-xs text-muted-foreground">{resources.enrichments.customRangeSeparator}</span>
              <Input
                type="datetime-local"
                value={customTo}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setCustomTo(event.target.value)}
                className="w-[190px]"
                aria-label={resources.enrichments.customToLabel}
              />
            </FilterGroup>
          )}

          <FilterGroup label={resources.enrichments.outcomeLabel}>
            {OUTCOME_OPTIONS.map((opt) => (
              <FilterChip
                key={opt.label}
                label={opt.label}
                active={outcome === opt.value}
                onClick={() => setOutcome(opt.value)}
                disabled={opt.disabled}
                title={opt.disabled ? resources.enrichments.unreachableOutcomeTooltip : undefined}
              />
            ))}
          </FilterGroup>

          <FilterGroup label={resources.enrichments.scopeLabel}>
            <Select
              value={scope ?? ''}
              onChange={(event: ChangeEvent<HTMLSelectElement>) => setScope(event.target.value || undefined)}
              className="w-44"
            >
              <option value="">{resources.enrichments.allScopes}</option>
              {ENRICHMENT_SCOPES.map((s) => (
                <option key={s} value={s}>
                  {scopeLabel(s)}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup label={resources.enrichments.propertyIdLabel}>
            <Input
              value={propertyId}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setPropertyId(event.target.value)}
              placeholder={resources.enrichments.propertyIdPlaceholder}
              className="w-40"
            />
          </FilterGroup>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="pb-1.5 text-[11.5px] font-medium text-primary hover:underline"
            >
              {resources.enrichments.clearFilters}
            </button>
          )}
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <span className="text-xs text-muted-foreground">{resultsLabel}</span>
        </div>

        {isError && (
          <div className="p-8 text-sm text-status-critical">
            <p>{resources.enrichments.loadError}</p>
            <p className="mt-1 font-mono text-xs text-muted-foreground">{errorMessage(error)}</p>
          </div>
        )}

        {!isError && !isLoading && items.length === 0 && (
          <div className="p-8 text-sm text-muted-foreground">{resources.enrichments.noResults}</div>
        )}

        {!isError && items.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{resources.enrichments.columns.propertyId}</TableHead>
                  <TableHead>{resources.enrichments.columns.scope}</TableHead>
                  <TableHead>{resources.enrichments.columns.outcome}</TableHead>
                  <TableHead>{resources.enrichments.columns.lastUpdated}</TableHead>
                  <TableHead>{resources.enrichments.columns.published}</TableHead>
                  <TableHead className="text-center">{resources.enrichments.columns.runs}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row: EnrichmentListRow) => (
                  <TableRow key={`${row.scope}:${row.propertyId}`}>
                    <TableCell className="font-mono text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate">{row.propertyId}</span>
                        <button
                          type="button"
                          className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                          onClick={() => setPropertyId(row.propertyId)}
                          aria-label={`Filter to property ${row.propertyId}`}
                          title="Filter to this property"
                        >
                          <ZoomIn className="h-3 w-3" />
                        </button>
                      </div>
                    </TableCell>
                    <TableCell>{scopeLabel(row.scope)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{outcomeLabel(row.result)}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground" title={formatUtcTooltip(toIso(row.timestamp))}>
                      {formatRunDate(toIso(row.timestamp))}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.publishedAt !== undefined ? (
                        <span title={formatUtcTooltip(toIso(row.publishedAt))}>{formatRunDate(toIso(row.publishedAt))}</span>
                      ) : (
                        resources.enrichments.unpublished
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <button
                        type="button"
                        className="text-xs font-medium text-primary hover:underline"
                        onClick={() => setOpenRunsFor({ scope: row.scope, propertyId: row.propertyId })}
                      >
                        {resources.enrichments.viewRuns}
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={openRunsFor !== null} onClose={() => setOpenRunsFor(null)} className="w-[820px]">
        {openRunsFor && (
          <>
            <DialogHeader>
              <div>
                <DialogTitle>{resources.enrichments.runsModalTitle}</DialogTitle>
                <DialogDescription>
                  {scopeLabel(openRunsFor.scope)} &middot; {openRunsFor.propertyId}
                  <br />
                  {resources.enrichments.runsModalSubtitle}
                </DialogDescription>
              </div>
              <DialogCloseButton onClick={() => setOpenRunsFor(null)} />
            </DialogHeader>
            <DialogBody>
              {runsQuery.isLoading && <p className="text-sm text-muted-foreground">{resources.common.loading}</p>}
              {runsQuery.isError && (
                <div className="text-sm text-status-critical">
                  <p>{resources.enrichments.runsLoadError}</p>
                  <p className="mt-1 font-mono text-xs text-muted-foreground">{errorMessage(runsQuery.error)}</p>
                </div>
              )}
              {!runsQuery.isLoading && !runsQuery.isError && runs.length === 0 && (
                <p className="text-sm text-muted-foreground">{resources.enrichments.noRuns}</p>
              )}
              {runs.length > 0 && (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead>{resources.runs.columns.runId}</TableHead>
                        <TableHead>{resources.runs.columns.status}</TableHead>
                        <TableHead>{resources.runs.columns.resultError}</TableHead>
                        <TableHead>{resources.runs.columns.duration}</TableHead>
                        <TableHead>{resources.runs.columns.cost}</TableHead>
                        <TableHead>{resources.runs.columns.model}</TableHead>
                        <TableHead>{resources.runs.columns.created}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {runs.map((row) => (
                        <TableRow key={row.runId}>
                          {/* `truncate` goes on the cell itself, not the inline Link/span -- an
                              inline element ignores overflow/text-overflow regardless of a
                              max-width on its block-level td ancestor, so the un-clipped run ID
                              would otherwise spill into the next column. */}
                          <TableCell className="max-w-[110px] truncate">
                            {hasEnrichmentDetailRoute(row.scope) ? (
                              <Link
                                to={runDetailHref(row)}
                                className="font-mono text-xs text-primary hover:underline"
                                title={`${row.runId} — view enrichment detail`}
                              >
                                {row.runId}
                              </Link>
                            ) : (
                              <span className="font-mono text-xs text-muted-foreground" title={row.runId}>
                                {row.runId}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap items-center gap-1">
                              <Badge variant={statusBadgeVariant(row.status)}>{statusLabel(row.status)}</Badge>
                              {row.result && <Badge variant="secondary">{outcomeLabel(row.result)}</Badge>}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-[220px] truncate text-muted-foreground" title={resultText(row)}>
                            {resultText(row)}
                          </TableCell>
                          <TableCell>{formatDuration(row.durationMs)}</TableCell>
                          <TableCell>{formatCost(row.costUsd)}</TableCell>
                          <TableCell className="text-muted-foreground">{row.model ?? '—'}</TableCell>
                          <TableCell className="text-muted-foreground" title={formatUtcTooltip(row.createdAt)}>
                            {formatRunDate(row.createdAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </DialogBody>
          </>
        )}
      </Dialog>
    </div>
  );
}
